<script>
    import {PATH_PREFIX} from '../../apiUrls.js';

    import {fade} from 'svelte/transition';
    import {draftElementDetails, elementDetails, elementInstance, standaloneEdit} from './workspacesStore.js';
    import Section from '../common/Section.svelte';
    import AssetExecutionDetails from '../Element/AssetExecutionDetails.svelte';
    import CorrectiveAction from '../Element/CorrectiveAction.svelte';

    import ElementDetails from './ElementView/ElementDetails.svelte';
    import KeyPointTable from './ElementView/KeyPointTable.svelte';
    import QuestionsTable from './ElementView/QuestionsTable.svelte';
    import ToolTable from './ElementView/ToolTable.svelte';
    import PartTable from './ElementView/PartTable.svelte';
    import HistoryTable from './ElementView/HistoryTable.svelte';
    import MaterialRisk from './ElementView/MaterialRisk.svelte';
    import RisksAndControls from '../Element/RisksAndControls/RisksAndControls.svelte';

    import {get} from '../../utils.js';
    import {sgettext} from '../../i18n.js';
    import ReferenceDocumentTable from "./ElementView/ReferenceDocumentTable.svelte";

    let versions = [];

    let details = {
        left_hand: $elementDetails,
        right_hand: $draftElementDetails ? $draftElementDetails : $elementDetails
    };

    $: getElementVersions($elementDetails);

    $: details = {
        left_hand: $elementDetails,
        right_hand: $draftElementDetails ? $draftElementDetails : $elementDetails
    };

    let leftHandSelectedVersion = details.left_hand.eevId;
    let rightHandSelectedVersion = details.right_hand.eevId;

    let loading = false;
    let leftHandLoading = false;
    let rightHandLoading = false;

    async function getElementVersions(eDetails) {
        loading = true;
        const eevId = eDetails ? eDetails.eevId : 0;

        if (eevId) {
            const response = await get(`${PATH_PREFIX}/sw/api/elements_database/element_version/${eevId}/element-versions/`);
            if (response.ok) {
                versions = await response.json();
                getVersionHistory('left_hand');
                getVersionHistory('right_hand');
            }
        }
        loading = false;
    }

    async function changeVersion(leftOrRight) {

        const eevId = leftOrRight === 'left_hand' ? leftHandSelectedVersion : rightHandSelectedVersion;

        if (leftOrRight === 'left_hand') {
            leftHandLoading = true;
        } else {
            rightHandLoading = true;
        }
        const param = $elementInstance && $elementInstance.id ? `?elementInstanceId=${$elementInstance.id}` : '';
        const response = await get(`${PATH_PREFIX}/sw/api/elements_database/elementelementversions/${eevId}/eev_detail/${param}`);
        if (response.ok) {
            details[leftOrRight] = await response.json();
            leftHandLoading = rightHandLoading = false;

            getVersionHistory(leftOrRight);
        }
    }

    async function getVersionHistory(leftOrRight) {
        const eevId = leftOrRight === 'left_hand' ? leftHandSelectedVersion : rightHandSelectedVersion;

        const response = await get(`${PATH_PREFIX}/sw/api/elements_database/elementelementversions/${eevId}/eev_history/`);
        if (response.ok) {
            details[leftOrRight].history = await response.json();
        }
    }

</script>

<style>
    .version-wrapper {
        min-width: 50%;
    }

    .version-selector-wrapper {
        background-color: #545b62;
    }

    #left-hand {
        border-right: 1px solid #c4c4c4;
    }

    .version-selector-wrapper select {
        display: inline-block;
        width: auto;
        background: white;
    }

    .wrapper {
        display: flex;
        flex-direction: column;
    }

    .wrapper > section {
        min-height: 80px;
        margin: 15px 5px;
    }

</style>


{#if details && details.left_hand && details.right_hand}
    {#if $standaloneEdit}
        <div class="title mb-3">
            <h3 class="text-primary">
                {details.left_hand.name}
            </h3>
            <h4>{sgettext("$$VERSIONS$$")}</h4>
        </div>
    {/if}
    {#if loading}
        <i class="fa fa-spinner fa-spin"></i>
    {:else}
        <div class="d-flex flex-row">

            <div id="left-hand" class="d-flex flex-column p-3 version-wrapper">
                {#if !leftHandLoading}
                    <div class="version-selector-wrapper p-2">
                        <span class="text-white font-weight-bold">{sgettext('$$VIEW$$')}:</span>
                        <select name="left-hand"
                                class="custom-select"
                                bind:value={leftHandSelectedVersion}
                                on:change={() => changeVersion('left_hand')}>
                            {#each versions as version}
                                <option value={version.id}
                                        selected={version.element_version.version_number === details.left_hand.version ? 'selected': ''}>
                                    {version.element_version.version_number} ({version.status_display})
                                </option>
                            {/each}
                        </select>
                    </div>

                    <div
                        transition:fade|local
                        class="wrapper">
                        {#if details.left_hand.materialRisk}
                            <br/>
                            <MaterialRisk
                                mechanism={details.left_hand.materialRisk.mechanism}
                                hazard={details.left_hand.materialRisk.hazard}
                                control={details.left_hand.materialRisk.control}
                                valid={details.left_hand.materialRisk.valid}
                                isVerticalLayout={true}
                            />
                        {/if}
                        <ElementDetails details={details.left_hand}/>
                        <Section title="{sgettext('$$RISKS_CONTROLS_1$$')}">
                            <RisksAndControls rows={details.left_hand.hazardscontrols} isVerticalLayout={true}/>
                        </Section>
                        {#if details.left_hand.type === 'PART'}
                            <Section title="{sgettext('$$PARTS$$')}">
                                <PartTable rows={details.left_hand.parts}/>
                            </Section>
                        {/if}
                        {#if details.left_hand.type === 'QUALITY' || details.left_hand.type === 'SAFETY' ||
                        details.left_hand.type === 'KNACK'}
                            <Section title="{sgettext('$$KEY_POINTS$$')}">
                                <KeyPointTable rows={details.left_hand.keypoints}/>
                            </Section>
                        {/if}
                        {#if details.left_hand.type === 'QUALITY' || details.left_hand.type === 'SAFETY'}
                            <Section title="{sgettext('$$QUESTIONS$$')}">
                                <QuestionsTable rows={details.left_hand.questions}/>
                            </Section>
                        {/if}

                        {#if !$standaloneEdit}
                            <Section title="{sgettext('$$ASSET_EXECUTION_DETAILS$$')}">
                                <AssetExecutionDetails model="{details.left_hand}"/>
                            </Section>
                        {/if}


                        <Section title="{sgettext('$$CORRECTIVE_ACTION$$')}">
                            <CorrectiveAction model="{details.left_hand}"/>
                        </Section>

                        {#if details.left_hand.type === 'TOOL'}
                            <Section title="{sgettext('$$TOOLS$$')}">
                                <ToolTable rows={details.left_hand.tools}/>
                            </Section>
                        {/if}

                        <Section title="{sgettext('$$REFERENCE_DOCUMENTS$$')}">
                            <ReferenceDocumentTable rows={details.left_hand.reference_documents}/>
                        </Section>


                        <Section title="{sgettext('$$HISTORY_1$$')}">
                            <HistoryTable rows={details.left_hand.history}/>
                        </Section>
                    </div>
                {/if}
            </div>
            <div id="right-hand" class="d-flex flex-column p-3 version-wrapper">
                {#if !rightHandLoading}
                    <div class="version-selector-wrapper p-2">
                        <span class="text-white font-weight-bold">{sgettext('$$COMPARE$$')}:</span>
                        <select name="right-hand"
                                class="custom-select"
                                bind:value={rightHandSelectedVersion}
                                on:change={() => changeVersion('right_hand')}>
                            {#each versions as version}
                                <option value={version.id}
                                        selected={version.element_version.version_number === details.right_hand.version ? 'selected': ''}>
                                    {version.element_version.version_number} ({version.status_display})
                                </option>
                            {/each}
                        </select>
                    </div>
                    <div
                        transition:fade|local
                        class="wrapper">
                        {#if details.right_hand.materialRisk}
                            <br/>
                            <MaterialRisk
                                mechanism={details.right_hand.materialRisk.mechanism}
                                hazard={details.right_hand.materialRisk.hazard}
                                control={details.right_hand.materialRisk.control}
                                valid={details.right_hand.materialRisk.valid}
                                isVerticalLayout={true}
                            />
                        {/if}
                        <ElementDetails details={details.right_hand}/>
                        <Section title="{sgettext('$$RISKS_CONTROLS_1$$')}">
                            <RisksAndControls rows={details.right_hand.hazardscontrols} isVerticalLayout={true}/>
                        </Section>
                        {#if details.right_hand.type === 'PART'}
                            <Section title="{sgettext('$$PARTS$$')}">
                                <PartTable rows={details.right_hand.parts}/>
                            </Section>
                        {/if}
                        {#if details.right_hand.type === 'QUALITY' || details.right_hand.type === 'SAFETY' || details.right_hand.type === 'KNACK'}
                            <Section title="{sgettext('$$KEY_POINTS$$')}">
                                <KeyPointTable rows={details.right_hand.keypoints}/>
                            </Section>
                        {/if}
                        {#if details.right_hand.type === 'QUALITY' || details.right_hand.type === 'SAFETY'}
                            <Section title="{sgettext('$$QUESTIONS$$')}">
                                <QuestionsTable rows={details.right_hand.questions}/>
                            </Section>
                        {/if}

                        {#if !$standaloneEdit}
                            <Section title="{sgettext('$$ASSET_EXECUTION_DETAILS$$')}">
                                <AssetExecutionDetails model="{details.right_hand}"/>
                            </Section>
                        {/if}


                        <Section title="{sgettext('$$CORRECTIVE_ACTION$$')}">
                            <CorrectiveAction model="{details.right_hand}"/>
                        </Section>

                        {#if details.right_hand.type === 'TOOL'}
                            <Section title="{sgettext('$$TOOLS$$')}">
                                <ToolTable rows={details.right_hand.tools}/>
                            </Section>
                        {/if}

                        <Section title="{sgettext('$$REFERENCE_DOCUMENTS$$')}">
                            <ReferenceDocumentTable rows={details.right_hand.reference_documents}/>
                        </Section>

                        <Section title="{sgettext('$$HISTORY_1$$')}">
                            <HistoryTable rows={details.right_hand.history}/>
                        </Section>


                    </div>

                {/if}

            </div>
        </div>
    {/if}
{/if}
